import { useAlertEvent, useAlerts, useExplainAlertEvent, type CauseLensRun } from "@/api/queries";
import { useFormatter, type ValueFormat } from "@/artifacts/format";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";

/**
 * "Explain this breach" (ALERT-3).
 *
 * The alert email links here, and **arriving spends nothing** — the event is
 * read, the move is shown, and Cause Lens runs only when somebody presses the
 * button that says it will cost model tokens (NFR-7). A confirm rather than a
 * second click, for the reason every other costly action here has one: it
 * says what is about to happen.
 */
export function ExplainBreachDialog({
  reportId,
  eventId,
  onClose,
  onStarted,
}: {
  reportId: string;
  eventId: string | null;
  onClose: () => void;
  onStarted: (run: CauseLensRun) => void;
}) {
  const formatter = useFormatter();
  const { data: event } = useAlertEvent(eventId);
  const { data: rules } = useAlerts({ kind: "report", id: reportId }, Boolean(eventId));
  const explain = useExplainAlertEvent(reportId);
  const rule = rules?.find((entry) => entry.id === event?.rule_id);
  const kind = (rule?.metric_format || "number") as ValueFormat;
  const figure = (value: number | null | undefined) =>
    value === null || value === undefined ? "—" : formatter.value(value, kind);

  const move = event
    ? `${rule?.metric_label ?? "The metric"} went from ${figure(event.previous_value)} to ${figure(event.value)}` +
      (event.delta_percent !== null && event.delta_percent !== undefined
        ? ` (${event.delta_percent > 0 ? "+" : ""}${event.delta_percent.toFixed(1)}%)`
        : "")
    : "Loading the breach…";

  return (
    <ConfirmDialog
      open={Boolean(eventId)}
      onClose={onClose}
      loading={explain.isPending}
      title="Explain this breach?"
      description={<>{move}.</>}
      consequence={
        event && !event.explainable ? (
          <>
            This breach cannot be explained any more — its snapshots are no longer kept, or it was
            not a breach. Run Cause Lens on the report instead.
          </>
        ) : (
          <>
            Cause Lens compares the two snapshots this alert fired between and asks the model what
            drove the change. It runs queries and spends model tokens, like asking a question.
          </>
        )
      }
      confirmLabel="Explain"
      onConfirm={() => {
        if (!eventId || !event?.explainable) return;
        explain.mutate(eventId, { onSuccess: onStarted });
      }}
    />
  );
}
